// components/admin/MarketersList.jsx   (ADMIN VIEW ALL MARKETERS HE CREATED)
import { useState, useEffect } from "react";
import { Mail, Phone, MapPin, Users, Building2, DollarSign, Calendar, Eye, X } from "lucide-react";

export default function MarketersList() {
  const [marketers, setMarketers] = useState([]);
  const [selectedMarketer, setSelectedMarketer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}my-marketers`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
    })
      .then(r => r.json())
      .then(d => {
        setMarketers(d.marketers || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center text-xl">Loading marketers...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-8 text-purple-700">My Marketers ({marketers.length})</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        <div className="bg-white p-6 rounded-xl shadow">
          <Users className="w-12 h-12 text-blue-600 mb-3" />
          <p className="text-4xl font-bold">{marketers.length}</p>
          <p className="text-gray-600">Total Marketers</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow">
          <DollarSign className="w-12 h-12 text-green-600 mb-3" />
          <p className="text-4xl font-bold">₦{marketers.reduce((a, m) => a + m.totalEarnings, 0)}</p>
          <p className="text-gray-600">Total Earnings</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow">
          <Users className="w-12 h-12 text-indigo-600 mb-3" />
          <p className="text-4xl font-bold">{marketers.reduce((a, m) => a + m.totalSeekers, 0)}</p>
          <p className="text-gray-600">Total Seekers</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow">
          <Building2 className="w-12 h-12 text-orange-600 mb-3" />
          <p className="text-4xl font-bold">{marketers.reduce((a, m) => a + m.totalEmployers, 0)}</p>
          <p className="text-gray-600">Total Employers</p>
        </div>
      </div>

      {/* Marketers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {marketers.map(m => (
          <div 
            key={m._id} 
            className="bg-white rounded-xl shadow hover:shadow-xl transition cursor-pointer p-6 border"
            onClick={() => setSelectedMarketer(m)}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold">{m.name}</h3>
                <p className="text-gray-600 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> {m.email}
                </p>
                <p className="text-gray-600 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {m.phone || "No phone"}
                </p>
                <p className="text-gray-600 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {m.state}, {m.lga}
                </p>
              </div>
              <Eye className="w-8 h-8 text-purple-600" />
            </div>

            <div className="space-y-3 mt-6">
              <div className="flex justify-between">
                <span className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> Seekers</span>
                <span className="font-bold text-xl">{m.totalSeekers}</span>
              </div>
              <div className="flex justify-between">
                <span className="flex items-center gap-2"><Building2 className="w-5 h-5 text-green-600赶" /> Employers</span>
                <span className="font-bold text-xl">{m.totalEmployers}</span>
              </div>
              <div className="flex justify-between">
                <span className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-600" /> Earnings</span>
                <span className="font-bold text-xl text-green-600">₦{m.totalEarnings}</span>
              </div>
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Joined: {new Date(m.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Modal */}
      {selectedMarketer && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto p-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-purple-700">{selectedMarketer.name}'s Full Report</h2>
              <button onClick={() => setSelectedMarketer(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="bg-blue-50 p-6 rounded-xl text-center">
                <Users className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <p className="text-4xl font-bold text-blue-700">{selectedMarketer.totalSeekers}</p>
                <p className="text-lg">Seekers Created</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl text-center">
                <Building2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="text-4xl font-bold text-green-700">{selectedMarketer.totalEmployers}</p>
                <p className="text-lg">Employers Created</p>
              </div>
              <div className="bg-purple-50 p-6 rounded-xl text-center">
                <DollarSign className="w-12 h-12 text-purple-600 mx-auto mb-3" />
                <p className="text-4xl font-bold text-purple-700">₦{selectedMarketer.totalEarnings}</p>
                <p className="text-lg">Total Earnings</p>
              </div>
            </div>

            {/* List of Seekers & Employers Created */}
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <h3 className="text-xl font-bold mb-4">Seekers Created</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedMarketer.seekers?.length > 0 ? selectedMarketer.seekers.map(s => (
                    <div key={s._id} className="bg-blue-50 p-4 rounded-lg">
                      <p className="font-bold">{s.firstName} {s.lastName}</p>
                      <p className="text-sm text-gray-600">{s.email}</p>
                    </div>
                  )) : <p className="text-gray-500">No seekers yet</p>}
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4">Employers Created</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedMarketer.employers?.length > 0 ? selectedMarketer.employers.map(e => (
                    <div key={e._id} className="bg-green-50 p-4 rounded-lg">
                      <p className="font-bold">{e.companyName}</p>
                      <p className="text-sm text-gray-600">{e.email}</p>
                    </div>
                  )) : <p className="text-gray-500">No employers yet</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}